import React from 'react';
import {
  TextField,
  DateField,
  NumberField,
  TextareaField,
  SelectField,
  ReadonlyField,
  SynonymsRadioField,
} from './fields';
import MultiselectField from './fields/MultiselectField';
import ListBuilder, {
  ListBuilderItem,
  ListBuilderConfig,
} from './fields/ListBuilder';
import { ModalMode } from './hooks/useEditableModal';

export interface ValidationResult {
  isValid: boolean;
  message?: string;
}

export interface FieldConfig<T> {
  key: keyof T;
  label: string;
  type:
    | 'text'
    | 'textarea'
    | 'number'
    | 'date'
    | 'datetime'
    | 'select'
    | 'readonly'
    | 'multiselect'
    | 'synonyms_radio'
    | 'list_builder';

  // Core value handling - using any for flexibility in field configurations
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValue: (entity: T) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue?: (entity: T, value: any) => T;

  // Formatting & validation
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (value: any) => string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validator?: (value: any) => ValidationResult;

  // Field-specific options
  options?: { value: string; label: string }[]; // For select fields
  placeholder?: string;
  min?: number; // For number/date fields
  max?: number; // For number/date fields
  step?: number; // For number fields

  // Behavior flags
  isAlwaysDisabled?: boolean;
  isRequired?: boolean;
  rows?: number; // For textarea

  // UI enhancements
  helpText?: string; // Helper text below field
  tooltipText?: string; // Tooltip text for field label
  maxLength?: number; // For text/textarea fields

  // List builder specific
  listBuilderConfig?: ListBuilderConfig;
  availableItems?: ListBuilderItem[];
}

interface ModalFieldRendererProps<T> {
  fields: FieldConfig<T>[];
  entity: T;
  setEntity: (entity: T) => void;
  mode: ModalMode;
  focusedField: string | null;
  setFocusedField: (field: string | null) => void;
  isFieldDisabled: (fieldName: string) => boolean;
  getFloatingLabelStyle: (
    fieldName: string,
    hasValue: boolean
  ) => React.CSSProperties;
  getFloatingInputStyle: (
    isTextarea: boolean,
    fieldName: string
  ) => React.CSSProperties;
  styles: Record<string, React.CSSProperties>;
  missingMessage?: string;
}

const ModalFieldRenderer = <T,>({
  fields,
  entity,
  setEntity,
  mode: _mode, // Renamed to avoid unused variable warning
  focusedField,
  setFocusedField,
  isFieldDisabled,
  getFloatingLabelStyle,
  getFloatingInputStyle,
  styles,
  missingMessage = 'Unknown',
}: ModalFieldRendererProps<T>) => {
  // State for field validation errors
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>(
    {}
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFieldChange = (field: FieldConfig<T>, newValue: any) => {
    if (!field.setValue) return;

    const fieldName = String(field.key);

    try {
      // Always update the field value first
      const newEntity = field.setValue(entity, newValue);
      setEntity(newEntity);
    } catch (error) {
      console.error(`❌ Failed to update field ${fieldName}:`, error);
      setFieldErrors(prev => ({
        ...prev,
        [fieldName]: 'Failed to update field value',
      }));
      return;
    }

    // Apply validation if provided (for display purposes)
    if (field.validator) {
      const validationResult = field.validator(newValue);
      if (!validationResult.isValid) {
        setFieldErrors(prev => ({
          ...prev,
          [fieldName]: validationResult.message || 'Invalid value',
        }));
      } else {
        // Clear error if validation passes
        setFieldErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    }
  };

  const renderField = (field: FieldConfig<T>) => {
    const fieldName = String(field.key);

    try {
      const rawValue = field.getValue(entity);
      const isDisabled = isFieldDisabled(fieldName);
      const isFocused = focusedField === fieldName;
      const hasError = !!fieldErrors[fieldName];

      // Determine if field has a value for floating label positioning
      let hasValue = false;
      if (field.type === 'number') {
        hasValue =
          rawValue !== null &&
          rawValue !== undefined &&
          typeof rawValue === 'number' &&
          !isNaN(rawValue);
      } else if (field.type === 'date' || field.type === 'datetime') {
        hasValue = !!rawValue;
      } else if (field.type === 'readonly') {
        hasValue = true; // Always true because ReadonlyField always uses missingMessage or formatted value
      } else {
        // For other field types, check if there's a formatted value if formatter exists
        if (field.formatter) {
          // Always try to format, even if rawValue is empty - this matches ReadonlyField logic
          const formattedValue = field.formatter(rawValue);
          hasValue = !!String(formattedValue || '').trim();
        } else {
          hasValue = !!String(rawValue || '').trim();
        }
      }

      // Enhanced label style with error support
      const getLabelStyle = () => {
        const baseStyle = getFloatingLabelStyle(fieldName, hasValue);
        return hasError
          ? { ...baseStyle, ...styles.floatingLabelErrorStyle }
          : baseStyle;
      };

      // Enhanced input style with error support
      const getInputStyle = (isTextarea: boolean = false) => {
        const baseStyle = getFloatingInputStyle(isTextarea, fieldName);
        return hasError
          ? { ...baseStyle, ...styles.floatingInputErrorStyle }
          : baseStyle;
      };

      const baseProps = {
        id: fieldName,
        label: field.label,
        value: rawValue,
        onChange: field.setValue
          ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (value: any) => handleFieldChange(field, value)
          : undefined,
        disabled: isDisabled,
        required: field.isRequired,
        placeholder: field.placeholder,
        onFocus: () => setFocusedField(fieldName),
        onBlur: () => setFocusedField(null),
        hasValue,
        isFocused,
        getFloatingLabelStyle: getLabelStyle,
        getFloatingInputStyle: getInputStyle,
        styles,
        missingMessage,
        maxLength: field.maxLength,
      };

      const renderFieldComponent = () => {
        switch (field.type) {
          case 'text':
            return (
              <TextField
                key={fieldName}
                {...baseProps}
                value={String(rawValue || '')}
                onChange={(value: string) => baseProps.onChange?.(value)}
              />
            );
          case 'textarea':
            return (
              <TextareaField
                key={fieldName}
                {...baseProps}
                value={String(rawValue || '')}
                onChange={(value: string) => baseProps.onChange?.(value)}
                rows={field.rows}
              />
            );
          case 'number':
            return (
              <NumberField
                key={fieldName}
                {...baseProps}
                value={typeof rawValue === 'number' ? rawValue : null}
                onChange={(value: number | null) => baseProps.onChange?.(value)}
                min={field.min}
                max={field.max}
                step={field.step}
              />
            );
          case 'date':
            return (
              <DateField
                key={fieldName}
                {...baseProps}
                value={String(rawValue || '')}
                onChange={(value: string) => baseProps.onChange?.(value)}
                includeTime={false}
              />
            );
          case 'datetime':
            return (
              <DateField
                key={fieldName}
                {...baseProps}
                value={String(rawValue || '')}
                onChange={(value: string) => baseProps.onChange?.(value)}
                includeTime={true}
              />
            );
          case 'select':
            return (
              <SelectField
                key={fieldName}
                {...baseProps}
                value={String(rawValue || '')}
                onChange={(value: string) => baseProps.onChange?.(value)}
                options={field.options || []}
              />
            );
          case 'multiselect':
            return (
              <MultiselectField
                key={fieldName}
                {...baseProps}
                value={Array.isArray(rawValue) ? rawValue : []}
                onChange={(value: string[]) => baseProps.onChange?.(value)}
                options={field.options || []}
              />
            );
          case 'readonly':
            return (
              <ReadonlyField
                key={fieldName}
                {...baseProps}
                value={String(rawValue || '')}
                formatter={field.formatter}
              />
            );
          case 'synonyms_radio':
            return (
              <SynonymsRadioField
                key={fieldName}
                label={field.label}
                preferredValue={String(rawValue?.preferredValue || '')}
                synonyms={
                  Array.isArray(rawValue?.synonyms) ? rawValue.synonyms : []
                }
                onChange={value => baseProps.onChange?.(value)}
                isDisabled={isDisabled}
                tooltipText={field.tooltipText}
                styles={styles}
                onFocus={() => setFocusedField(fieldName)}
                onBlur={() => setFocusedField(null)}
                hasValue={!!rawValue?.preferredValue}
                isFocused={focusedField === fieldName}
                getFloatingLabelStyle={getFloatingLabelStyle}
                getFloatingInputStyle={getFloatingInputStyle}
              />
            );
          case 'list_builder':
            return (
              <ListBuilder
                key={fieldName}
                id={fieldName}
                label={field.label}
                value={Array.isArray(rawValue) ? rawValue : []}
                onChange={(value: ListBuilderItem[]) =>
                  baseProps.onChange?.(value)
                }
                availableItems={field.availableItems || []}
                config={field.listBuilderConfig || ({} as ListBuilderConfig)}
                disabled={isDisabled}
                required={field.isRequired}
                styles={styles}
              />
            );
          default:
            console.error(
              `❌ Unknown field type: ${field.type} for field: ${fieldName}`
            );
            // Fallback to text field with error styling
            return (
              <TextField
                key={fieldName}
                {...baseProps}
                value={String(rawValue || '')}
                onChange={(value: string) => baseProps.onChange?.(value)}
              />
            );
        }
      };

      return (
        <div key={fieldName} style={styles.fieldContainer}>
          {renderFieldComponent()}
          {/* Error message */}
          {hasError && (
            <span style={styles.fieldErrorMessageStyle}>
              {fieldErrors[fieldName]}
            </span>
          )}
          {/* Help text (only show if no error) */}
          {!hasError && field.helpText && (
            <span style={styles.fieldHelpTextStyle}>{field.helpText}</span>
          )}
        </div>
      );
    } catch (error) {
      console.error(`❌ Failed to render field ${fieldName}:`, error);
      return (
        <div key={fieldName} style={styles.fieldContainer}>
          <div style={{ color: '#ef4444', padding: '8px', fontSize: '14px' }}>
            ❌ Error rendering field "{field.label || fieldName}"
          </div>
        </div>
      );
    }
  };

  return <>{fields.map(renderField)}</>;
};

export default React.memo(ModalFieldRenderer) as typeof ModalFieldRenderer;
